import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setStaffInfo } from 'redux/actions'
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import FormContainer from 'components/FormContainer';
import PhoneNumberInput from 'components/PhoneNumberInput';
import Input from 'components/Input';
import Dropdown from 'components/Dropdown';
import Checkbox from 'components/Checkbox';
import { useQuery } from '@apollo/client';
import { GET_ALL_USER_ROLES } from 'graphql/service';
import { usePlacesWidget } from 'react-google-autocomplete'
import { debouncedErrorMessage } from 'utils/messageUtil';

const StaffInformation = ({
  staffInfoError,
  setStaffInfoError,
  btnInfo
}) => {

  const dispatch = useDispatch()
  const staffInfo = useSelector(state => state.staffInfoReducer.staffInfo)
  const [phoneNumber, setPhoneNumber] = useState(staffInfo.phoneNumber || "")
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false)

  useEffect(() => {
    if (staffInfo.phoneNumber && staffInfo.phoneNumber !== phoneNumber) {
      setPhoneNumber(staffInfo.phoneNumber);
    }
  }, [staffInfo.phoneNumber]);

  useEffect(() => {
    if (phoneNumberValidity && phoneNumber !== staffInfo.phoneNumber) {
      setStaffInfoError({ ...staffInfoError, phoneNumberError: false });
      dispatch(
        setStaffInfo({
          phoneNumber: phoneNumber,
        })
      );
    }
  }, [phoneNumberValidity]);

  const { ref } = usePlacesWidget({
    apiKey: `${process.env.REACT_APP_GOOGLE_APIKEY}&loading=async`,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city = getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      dispatch(
        setStaffInfo({
          streetAddress,
          streetAddressLine2,
          city,
          province,
          postalCode,
          country
        })
      );
    },
    options: {
      types: ['address'],
    },
  }) 

  const {data, error} = useQuery(GET_ALL_USER_ROLES)

  const roleOptions = useMemo(() => {
    if (!data?.validUserRoles?.length) return [];
    return data.validUserRoles.map(role => ({ label: role.name, value: role.roleId }));
  }, [data]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  return (
    <Card>
      <CardHeader
        additionalstyle={`
          @media only screen and (max-width: 767px) {
            
            .type {
              width: 100%;
              margin-bottom: 10px; 
            }

            .buttonContainer {
              width: 100%;
              justify-content: space-between;
            }
            
            flex-direction: column;
            justify-content: center;
          }
        `}
      >
        <div className='type'>STAFF INFORMATION</div>
      </CardHeader>
      <FormContainer>
        <Input
          mainPage
          title="NAME"
          value={staffInfo.fullName || ''}
          error={staffInfoError.fullNameError}
          onChange={(e) => {
            dispatch(setStaffInfo({fullName: e.target.value}))
          }}
          additionalstyle={`
            flex-basis: 49%;
            margin-right:2%;
            @media only screen and (max-width: 767px) {
              flex-basis: 100%;
              margin-right: 0;
            }
          `}
        />
        <Dropdown
          mainPage
          options={roleOptions}
          value={roleOptions.find(option => option.value === staffInfo.roleId)?.label || ''}
          error={staffInfoError.rolesError}
          title="ROLE"
          onChange={(e) => {
            dispatch(setStaffInfo({role: e}))
          }}
          additionalstyle={`
            flex-basis: 49%;
            @media only screen and (max-width: 767px) {
              flex-basis: 100%;
              margin-right: 0;
            }
          `}
        />
        <Input
          mainPage
          title="ADDRESS"
          inputFieldRef={ref}
          value={staffInfo.streetAddress || ''}
          error={staffInfoError.streetAddressError}
          onChange={(e) => {
            dispatch(setStaffInfo({streetAddress: e.target.value}))
          }}
          additionalstyle={`
            flex-basis: 100%;
          `}
        />
        <Input
          mainPage
          title="EMAIL"
          value={staffInfo.email || ''}
          error={staffInfoError.emailError}
          onChange={(e) => {
            dispatch(setStaffInfo({email: e.target.value}))
          }}
          additionalstyle={`
            flex-basis: 49%;
            margin-right:2%;
            @media only screen and (max-width: 767px) {
              flex-basis: 100%;
              margin-right: 0;
            }
          `}
        />
        <PhoneNumberInput
          mainPage
          title="PHONE"
          error={staffInfoError.phoneNumberError}
          initialValue={phoneNumber}
          onChangeNumber={setPhoneNumber}
          setPhoneNumberValidity={setPhoneNumberValidity}
          onBlur={() => {
            if(!phoneNumberValidity && phoneNumber.length>0) {
              setStaffInfoError({...staffInfoError, phoneNumberError: true})
            } else {
              setStaffInfoError({...staffInfoError, phoneNumberError: false})
            }
          }}
          additionalstyle={`
            flex-basis: 49%;
            @media only screen and (max-width: 767px) {
              flex-basis: 100%;
            }
          `}
        />
        <Checkbox
          checked={!staffInfo.isActive}
          onChange={() => dispatch(setStaffInfo({isActive: !staffInfo.isActive}))}
          title="Deactivate Account"
          additionalstyle={`
            margin: 24px 0 0 2px;
          `}
        />
      </FormContainer>
      <div className='buttonContainer'>{btnInfo}</div>
    </Card>
  );
};

export default StaffInformation;
